// VendAgain — "In Search Of" browse. Public list of wanted listings.
// Buyers see their own ISO alerts here + on /vendagain/iso/mine.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Plus, Compass, ArrowLeft, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, SECTIONS, CAT_MAP, CategoryChip, formatPrice } from "./shared";

export default function VendAgainIsoBrowse() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [section, setSection] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (city.trim()) params.city = city.trim();
        if (section) params.section = section;
        if (category) params.category = category;
        const { data } = await api.get("/vendagain/iso", { params });
        setItems(data.items || []);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, city, section, category]);

  const visibleCategories = section
    ? CATEGORIES.filter((c) => c.section === section)
    : CATEGORIES;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14" data-testid="vendagain-iso-browse">
      <Link to="/vendagain" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft size={14} /> Back to VendAgain
      </Link>

      <div className="relative overflow-hidden rounded-sm p-10 md:p-14 mt-4 mb-10"
           style={{ background: "linear-gradient(135deg, var(--moss) 0%, var(--avt-teal) 100%)" }}>
        <div className="relative text-white max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-4 font-bold"
               style={{ background: "rgba(255,255,255,0.18)" }}>
            <Compass className="h-3 w-3" /> In Search Of
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
            Tell the community what you&rsquo;re hunting for.
          </h1>
          <p className="mt-4 text-base sm:text-lg opacity-95">
            Post a "looking for" wish and we&rsquo;ll ping you the moment a
            matching item drops. Sellers browsing here can also reach out
            directly.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {user ? (
              <Link to="/vendagain/iso/new" data-testid="iso-new-btn">
                <Button className="btn-primary rounded-full inline-flex items-center gap-2">
                  <Plus size={16} /> Post a wish
                </Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button className="btn-primary rounded-full">Sign up to save searches</Button>
              </Link>
            )}
            {user && (
              <Link to="/vendagain/iso/mine" data-testid="iso-mine-btn">
                <Button variant="outline" className="rounded-full border-white text-white hover:bg-white/10">
                  My alerts
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters — same shape as the for-sale browse for consistency */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
            <Input placeholder="Search wishes…" value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9 rounded-full"
                    data-testid="iso-search-input" />
          </div>
          <div className="relative min-w-[180px]">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
            <Input placeholder="City" value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="pl-9 rounded-full"
                    data-testid="iso-city-input" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button"
                    onClick={() => { setSection(""); setCategory(""); }}
                    data-testid="iso-section-all"
                    className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition ${!section ? "" : "opacity-60"}`}
                    style={{
                      background: !section ? "var(--avt-purple)" : "rgba(0,0,0,0.06)",
                      color: !section ? "#fff" : "var(--ink-soft)",
                    }}>All</button>
          {SECTIONS.map((s) => (
            <button key={s.key} type="button"
                      data-testid={`iso-section-${s.key}`}
                      onClick={() => { setSection(s.key); setCategory(""); }}
                      className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition ${section === s.key ? "" : "opacity-60"}`}
                      style={{
                        background: section === s.key ? "var(--avt-purple)" : "rgba(0,0,0,0.06)",
                        color: section === s.key ? "#fff" : "var(--ink-soft)",
                      }}>{s.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {visibleCategories.map((c) => {
            const active = category === c.value;
            const Icon = c.icon;
            return (
              <button key={c.value} type="button"
                        onClick={() => {
                          const next = active ? "" : c.value;
                          setCategory(next);
                          if (next && !section) setSection(c.section);
                        }}
                        data-testid={`iso-cat-filter-${c.value}`}
                        className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full transition ${active ? "ring-2 ring-offset-1" : ""}`}
                        style={{ background: c.color, color: c.ink }}>
                <Icon size={12} /> {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center opacity-60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center border-2 border-dashed rounded-sm"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
              data-testid="iso-empty">
          <Compass className="mx-auto h-10 w-10 opacity-30" />
          <h3 className="mt-4 font-serif text-2xl">Nobody&rsquo;s posted a wish yet.</h3>
          <p className="mt-2 text-sm opacity-70">Be first — sellers browsing here will see it right away.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((i) => {
            const cat = CAT_MAP[i.category];
            const Icon = cat?.icon;
            return (
              <div key={i.id}
                    className="card-warm rounded-sm p-5"
                    data-testid={`iso-card-${i.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <CategoryChip value={i.category} />
                  <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(111,125,85,0.14)", color: "var(--moss)" }}>
                    Wanted
                  </span>
                </div>
                <h3 className="font-serif text-xl leading-tight">{i.title}</h3>
                <p className="mt-2 text-sm opacity-75 line-clamp-3">{i.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1 opacity-70">
                    <MapPin size={11} /> {i.city}
                  </span>
                  {i.max_price_cents ? (
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--avt-purple)" }}>
                      <DollarSign size={11} /> up to {formatPrice(i.max_price_cents, false)}
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 pt-3 border-t text-xs opacity-70" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  Posted by {i.owner.slug ? (
                    <Link to={`/vendors/${i.owner.slug}`} className="underline decoration-dotted underline-offset-4">
                      {i.owner.name}
                    </Link>
                  ) : i.owner.name}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
